/**
 * Auth Result Classes
 *
 * Discriminated result types returned by auth operations (login, verifyMFA).
 * Each class carries only the fields relevant to its state and provides
 * type-guard methods for ergonomic narrowing in consuming code.
 *
 * Usage:
 * ```ts
 * const result = await login(identifier, password)
 *
 * if (result.requiresMFA()) {
 *   // TypeScript knows result is AuthMFAChallenge here
 *   console.log(result.challengeToken, result.availableMethods)
 * } else if (result.requiresPasswordChange()) {
 *   // TypeScript knows result is AuthMustReset here
 *   await resetPassword(result.challengeToken, newPassword)
 * } else if (result.requiresMFAEnrollment()) {
 *   // TypeScript knows result is AuthMFAEnrollRequired here
 *   await beginEnrollWithChallenge(result, 'totp')
 * } else if (result.isSuccess()) {
 *   // TypeScript knows result is AuthSuccess here
 *   console.log(result.userId, result.tenantId)
 * }
 * ```
 */

import type {
  LoginResponse,
  MFASwitchResponse,
  MFAVerifyResponse,
  ResetAccountOption,
  ResetFlowResponse,
} from './types';

// =============================================================================
// Result Classes
// =============================================================================

/** Login succeeded — session cookie set, legacy token fetched. */
export class AuthSuccess {
  readonly kind = 'success' as const;
  readonly userId: string;
  readonly tenantId: number;
  readonly mfaRecommended: boolean;
  /**
   * The user's first backup codes, set only when MFA setup during login just
   * issued them (PRX-2159). Shown once and never again. Empty otherwise.
   */
  readonly backupCodes: string[];

  constructor(response: LoginResponse | MFAVerifyResponse) {
    this.userId = response.userId ?? '';
    this.tenantId = response.tenantId ?? 0;
    this.mfaRecommended = 'mfaRecommended' in response ? (response.mfaRecommended ?? false) : false;
    this.backupCodes = 'backupCodes' in response ? (response.backupCodes ?? []) : [];
  }

  isSuccess(): this is AuthSuccess {
    return true;
  }
  requiresMFA(): this is AuthMFAChallenge {
    return false;
  }
  requiresPasswordChange(): this is AuthMustReset {
    return false;
  }
  requiresMFAEnrollment(): this is AuthMFAEnrollRequired {
    return false;
  }
}

/** MFA verification required — use verifyMFA(), resendMFA(), or switchMFAMethod(). */
export class AuthMFAChallenge {
  readonly kind = 'mfa_required' as const;
  readonly challengeToken: string;
  readonly mfaMethod: string | undefined;
  readonly availableMethods: string[];
  readonly codeSent: boolean;

  constructor(response: LoginResponse | MFASwitchResponse | ResetFlowResponse) {
    this.challengeToken = response.challengeToken ?? '';
    this.mfaMethod = response.mfaMethod ?? undefined;
    this.availableMethods = response.availableMethods ?? [];
    this.codeSent = response.codeSent ?? false;
  }

  isSuccess(): this is AuthSuccess {
    return false;
  }
  requiresMFA(): this is AuthMFAChallenge {
    return true;
  }
  requiresPasswordChange(): this is AuthMustReset {
    return false;
  }
  requiresMFAEnrollment(): this is AuthMFAEnrollRequired {
    return false;
  }
}

/**
 * Password must be reset before access is granted.
 *
 * The user has no session at this point, so challengeToken is the only
 * credential they hold — spend it on resetPassword(). The backend sends no
 * identity fields for this status, by design.
 */
export class AuthMustReset {
  readonly kind = 'must_reset' as const;
  readonly challengeToken: string;
  /**
   * The user's first backup codes, when MFA setup during login issued them
   * just before the password change (PRX-2159). Empty otherwise.
   */
  readonly backupCodes: string[];

  constructor(response: LoginResponse | MFAVerifyResponse | ResetFlowResponse) {
    this.challengeToken = response.challengeToken ?? '';
    this.backupCodes = 'backupCodes' in response ? (response.backupCodes ?? []) : [];
  }

  isSuccess(): this is AuthSuccess {
    return false;
  }
  requiresMFA(): this is AuthMFAChallenge {
    return false;
  }
  requiresPasswordChange(): this is AuthMustReset {
    return true;
  }
  requiresMFAEnrollment(): this is AuthMFAEnrollRequired {
    return false;
  }
}

/**
 * The password was right, but the user holds a role that requires MFA and has
 * no TOTP or SMS method yet (PRX-2355). They get no session until they set one
 * up. Login returns this, or MFA verify does after the email code of a user
 * whose only method is email.
 *
 * challengeToken is a setup token: spend it on beginEnrollWithChallenge() and
 * confirmEnrollWithChallenge(). It lasts 15 minutes and is good for nothing
 * else. availableMethods lists the methods the user may set up.
 */
export class AuthMFAEnrollRequired {
  readonly kind = 'mfa_enroll_required' as const;
  readonly challengeToken: string;
  readonly availableMethods: string[];

  constructor(response: LoginResponse | MFAVerifyResponse) {
    this.challengeToken = response.challengeToken ?? '';
    this.availableMethods = response.availableMethods ?? [];
  }

  isSuccess(): this is AuthSuccess {
    return false;
  }
  requiresMFA(): this is AuthMFAChallenge {
    return false;
  }
  requiresPasswordChange(): this is AuthMustReset {
    return false;
  }
  requiresMFAEnrollment(): this is AuthMFAEnrollRequired {
    return true;
  }
}

/**
 * Several accounts share the address, so the user has to say which one.
 *
 * Only ever reached after a code sent to that address has verified, which is
 * what makes listing the accounts safe. Offering this any earlier would turn the
 * flow into a way of asking whether an address has accounts — see the settled
 * design in PRX-2034.
 */
export class ResetAccountChoice {
  readonly kind = 'select_account' as const;
  readonly challengeToken: string;
  readonly accounts: ResetAccountOption[];

  constructor(response: ResetFlowResponse) {
    this.challengeToken = response.challengeToken ?? '';
    this.accounts = response.accounts ?? [];
  }

  isSuccess(): this is AuthSuccess {
    return false;
  }
  requiresMFA(): this is AuthMFAChallenge {
    return false;
  }
  requiresPasswordChange(): this is AuthMustReset {
    return false;
  }
  requiresMFAEnrollment(): this is AuthMFAEnrollRequired {
    return false;
  }
}

// =============================================================================
// Union Type + Factory
// =============================================================================

/** Discriminated union of all possible auth outcomes. */
export type AuthResult = AuthSuccess | AuthMFAChallenge | AuthMustReset | AuthMFAEnrollRequired;

/**
 * What a forgot-password step can hand back.
 *
 * `success` is absent on purpose: typing a code is not a way in, so the flow
 * always ends at `must_reset` and the user still has to set a password.
 *
 * Narrow this with `kind` rather than the type-guard methods. The guards answer
 * the three login questions and there is no `requiresAccountChoice()` among
 * them; `kind` is on every class already and covers all four cases.
 */
export type ResetFlowResult = ResetAccountChoice | AuthMFAChallenge | AuthMustReset;

/** Maps a raw LoginResponse to the appropriate result class based on status. */
export function toAuthResult(response: LoginResponse): AuthResult {
  switch (response.status) {
    case 'success':
      return new AuthSuccess(response);
    case 'mfa_required':
      return new AuthMFAChallenge(response);
    case 'must_reset':
      return new AuthMustReset(response);
    case 'mfa_enroll_required':
      return new AuthMFAEnrollRequired(response);
    case 'select_account':
      // LoginStatus is shared with the forgot-password flow, which is the only
      // thing that returns this. /auth/login cannot, so treat it as a bug.
      throw new Error('Unexpected select_account status from login');
    default: {
      const _exhaustive: never = response.status;
      throw new Error(`Unknown login status: ${_exhaustive}`);
    }
  }
}

/**
 * Maps a forgot-password step's response to a result class.
 *
 * `endpoint` only ever appears in the error text, to say which step produced a
 * status it should not have. A caller that cannot accept every status — the
 * account step cannot be told to choose again — rejects it before calling here.
 */
export function toResetFlowResult(response: ResetFlowResponse, endpoint: string): ResetFlowResult {
  switch (response.status) {
    case 'select_account':
      return new ResetAccountChoice(response);
    case 'mfa_required':
      return new AuthMFAChallenge(response);
    case 'must_reset':
      return new AuthMustReset(response);
    case 'success':
      // Verifying a code proves control of the mailbox, not of the account, so
      // it can never be a way in. If this ever fires, something upstream has
      // started handing out sessions and failing loudly is the safe response.
      throw new Error(`Unexpected success status from ${endpoint}`);
    case 'mfa_enroll_required':
      // Only login asks a user to set up MFA. A reset that reaches a user who
      // must set it up ends at must_reset, and their next login asks.
      throw new Error(`Unexpected mfa_enroll_required status from ${endpoint}`);
    default: {
      const _exhaustive: never = response.status;
      throw new Error(`Unknown reset flow status: ${_exhaustive}`);
    }
  }
}
